// Typed client for the Worker's /api routes. Shapes mirror src/mailbox/mailbox-do.ts.

export interface EmailAddress {
  name: string;
  address: string;
}

export interface Me {
  user: { email: string; username: string; name: string | null; isAdmin: boolean };
  credentials: CredentialSummary | null;
  imapDefaults: { host: string; port: number };
}

export interface CredentialSummary {
  host: string;
  port: number;
  user: string;
  verifiedAt: number;
  needsPassword: boolean;
  lastAuthError: string | null;
  updatedAt: number;
}

export type MailboxState = "new" | "active" | "paused" | "needs_password" | "wiping";

export interface FolderReport {
  name: string;
  display: string;
  label: string;
  role: string | null;
  hiddenByDefault: boolean;
  serverMessages: number | null;
  serverUnseen: number | null;
  indexed: number;
  oldest: string | null;
  newest: string | null;
  backfillDone: boolean;
  newMailPending: boolean;
  lastSync: string | null;
  excluded: boolean;
}

export interface MailboxReport {
  state: MailboxState;
  email: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  nextSyncAt: string | null;
  serverCheckedAt: string | null;
  syncing: boolean;
  folders: FolderReport[];
  totals: { serverMessages: number; indexed: number; percent: number };
  embedding: { pendingChunks: number; vectors: number; lastError: string | null };
  footprint: { databaseBytes: number; rawMailBytes: number; vectors: number };
  wipe: { vectorsRemaining: number } | null;
  exclusions: Exclusions & { skippedMessages: number };
  quarantine: { count: number; last: string | null };
}

export interface Exclusions {
  senders: string[];
  folders: string[];
}

export interface SearchHit {
  id: string;
  subject: string;
  from: EmailAddress;
  date: string | null;
  folders: string[];
  hasAttachments: boolean;
  unread: boolean;
  replied: boolean;
  flagged: boolean;
  snippet: string;
  matchedBy: ("keyword" | "semantic" | "pattern")[];
}

export interface MessageDetail {
  id: string;
  messageId: string | null;
  subject: string;
  from: EmailAddress;
  to: EmailAddress[];
  cc: EmailAddress[];
  date: string | null;
  folders: { name: string; flags: string[] }[];
  body: string;
  partial: boolean;
  attachments: { index: number; filename: string; mime: string; size: number; status: string; detail: string | null; text: string | null }[];
}

export interface ThreadMessage {
  id: string;
  subject: string;
  from: EmailAddress;
  to: EmailAddress[];
  date: string | null;
  folders: string[];
  hasAttachments: boolean;
  unread: boolean;
  replied: boolean;
  flagged: boolean;
  body: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? "error", data.message ?? `Request failed (${res.status})`);
  return data as T;
}

export const api = {
  me: () => request<Me>("GET", "/api/me"),
  status: () => request<{ status: MailboxReport; credentials: CredentialSummary | null }>("GET", "/api/status"),
  saveCredentials: (body: { password: string; host?: string; port?: string }) => request<{ ok: true }>("POST", "/api/credentials", body),
  removeCredentials: () => request<{ ok: true }>("DELETE", "/api/credentials"),
  syncNow: () => request<{ ok: true }>("POST", "/api/sync"),
  pause: () => request<{ ok: true }>("POST", "/api/pause"),
  resume: () => request<{ ok: true }>("POST", "/api/resume"),
  search: (params: Record<string, string>) =>
    request<{ results: SearchHit[] }>("GET", `/api/search?${new URLSearchParams(params).toString()}`),
  message: (id: string) => request<{ message: MessageDetail }>("GET", `/api/messages/${encodeURIComponent(id)}`),
  thread: (id: string) =>
    request<{ thread: { messages: ThreadMessage[]; truncated: boolean } }>("GET", `/api/messages/${encodeURIComponent(id)}/thread`),
  setExclusions: (body: Exclusions) =>
    request<{ exclusions: Exclusions; removedMessages: number; rescanning: boolean }>("PUT", "/api/exclusions", body),
  deleteMyData: (confirm: string) => request<{ ok: true }>("POST", "/api/delete-my-data", { confirm }),
};

export interface AdminUser {
  id: string;
  email: string;
  username: string;
  name: string | null;
  status: "active" | "disabled";
  createdAt: number;
  lastLoginAt: number | null;
  isAdmin: boolean;
  credentials: CredentialSummary | null;
  grants: number;
  mailbox: MailboxReport | null;
  mailboxError: string | null;
}

export interface AuditEntry {
  id: number;
  at: number;
  user_id: string | null;
  email: string | null;
  action: string;
  detail: string | null;
}

export const adminApi = {
  users: () => request<{ users: AdminUser[] }>("GET", "/api/admin/users"),
  audit: (limit = 100) => request<{ entries: AuditEntry[] }>("GET", `/api/admin/audit?limit=${limit}`),
  act: (userId: string, action: "reconcile" | "rebuild" | "disable" | "enable") =>
    request<{ ok: true }>("POST", `/api/admin/users/${encodeURIComponent(userId)}/${action}`, {}),
  remove: (userId: string, confirm: string) =>
    request<{ ok: true }>("POST", `/api/admin/users/${encodeURIComponent(userId)}/delete`, { confirm }),
};
